import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Smartphone,
  Download,
  QrCode,
  Shield,
  Bell,
  CreditCard,
  Fingerprint,
  Lock,
  Zap,
  Check,
  Apple,
  Chrome,
  Star,
  TrendingUp,
  Users,
  Globe,
  ExternalLink,
  ChevronRight
} from 'lucide-react'

const features = [
  {
    id: 1,
    title: 'Biometric Security',
    description: 'Face ID and fingerprint authentication for secure access',
    icon: Fingerprint,
    color: 'bg-blue-50 text-blue-600'
  },
  {
    id: 2,
    title: 'Instant Notifications',
    description: 'Real-time alerts for all transactions and account activity',
    icon: Bell,
    color: 'bg-purple-50 text-purple-600'
  },
  {
    id: 3,
    title: 'Mobile Deposits',
    description: 'Deposit checks instantly using your camera',
    icon: CreditCard,
    color: 'bg-emerald-50 text-emerald-600'
  },
  {
    id: 4,
    title: 'Quick Transfers',
    description: 'Send money to anyone in seconds',
    icon: Zap,
    color: 'bg-orange-50 text-orange-600'
  },
  {
    id: 5,
    title: 'Enhanced Security',
    description: 'Bank-grade encryption and fraud protection',
    icon: Shield,
    color: 'bg-red-50 text-red-600'
  },
  {
    id: 6,
    title: 'Card Controls',
    description: 'Lock, unlock, and manage your cards on the go',
    icon: Lock,
    color: 'bg-slate-50 text-slate-600'
  }
]

const appStats = [
  { label: 'Downloads', value: '2M+', icon: Download },
  { label: 'Rating', value: '4.8', icon: Star },
  { label: 'Active Users', value: '500K+', icon: Users },
  { label: 'Countries', value: '50+', icon: Globe }
]

const faqs = [
  {
    question: 'Is the mobile app free to download?',
    answer: 'Yes, the Swift Gold Fiduciary mobile app is completely free to download and use for all account holders.'
  },
  {
    question: 'What devices are supported?',
    answer: 'The app is available for iOS 14+ and Android 8.0+. It works on both phones and tablets.'
  },
  {
    question: 'Is mobile banking secure?',
    answer: 'Absolutely. We use bank-grade encryption, biometric authentication, and multi-factor authentication to keep your account secure.'
  },
  {
    question: 'Can I deposit checks using the app?',
    answer: 'Yes, you can deposit checks by taking a photo with your device camera. Funds are typically available within 1-2 business days.'
  }
]

export default function MobilePage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [expandedFaq, setExpandedFaq] = useState(null)

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Mobile Banking</h1>
              <p className="text-sm text-slate-500 mt-0.5">Bank anywhere, anytime from your device</p>
            </div>

            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium">
                <Download className="w-4 h-4" />
                Download App
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        {/* Tabs */}
        <div className="flex gap-2 bg-slate-100 rounded-lg p-1 mb-8">
          {['overview', 'features', 'download', 'faq'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2.5 rounded-md transition-colors capitalize text-sm font-medium ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Swift Gold Fiduciary Mobile</h2>
                    <p className="text-slate-400 text-sm">Your bank, in your pocket</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm max-w-2xl">
                  Experience the power of modern banking with our award-winning mobile app.
                  Manage your accounts, transfer money, and stay on top of your finances from anywhere.
                </p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid md:grid-cols-4 gap-6">
              {appStats.map((stat, index) => {
                const IconComponent = stat.icon
                return (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-5 bg-white border border-slate-200 rounded-xl"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-medium text-slate-500">{stat.label}</h3>
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-4 h-4 text-slate-600" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  </motion.div>
                )
              })}
            </div>

            {/* Quick Access */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-6">What You Can Do</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Check balances and transaction history',
                  'Transfer money between accounts',
                  'Pay bills and schedule payments',
                  'Deposit checks with your camera',
                  'Lock and unlock your cards instantly',
                  'Set up custom spending alerts',
                  'Find nearby ATMs and branches',
                  'Chat with customer support 24/7'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all"
                >
                  <div className={`w-12 h-12 rounded-lg ${feature.color} flex items-center justify-center mb-4`}>
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{feature.description}</p>
                </motion.div>
              )
            })}
          </div>
        )}

        {/* Download Tab */}
        {activeTab === 'download' && (
          <div className="space-y-8">
            <div className="bg-white border border-slate-200 rounded-xl p-8">
              <div className="text-center max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Download className="w-8 h-8 text-slate-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Download Swift Gold Fiduciary Mobile</h2>
                <p className="text-sm text-slate-500 mb-8">
                  Available for iOS and Android devices. Get started in minutes.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                  <button className="flex items-center gap-3 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all">
                    <Apple className="w-6 h-6" />
                    <div className="text-left">
                      <div className="text-xs">Download on the</div>
                      <div className="text-sm font-semibold">App Store</div>
                    </div>
                  </button>

                  <button className="flex items-center gap-3 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all">
                    <Chrome className="w-6 h-6" />
                    <div className="text-left">
                      <div className="text-xs">Get it on</div>
                      <div className="text-sm font-semibold">Google Play</div>
                    </div>
                  </button>
                </div>

                <div className="border-t border-slate-200 pt-8">
                  <p className="text-xs text-slate-500 mb-4">Or scan the QR code with your device</p>
                  <div className="w-32 h-32 bg-slate-100 rounded-lg mx-auto flex items-center justify-center">
                    <QrCode className="w-16 h-16 text-slate-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* System Requirements */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-6">System Requirements</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Apple className="w-4 h-4" />
                    iOS
                  </h4>
                  <ul className="space-y-2 text-xs text-slate-500">
                    <li>• iOS 14.0 or later</li>
                    <li>• iPhone, iPad, and iPod touch</li>
                    <li>• 50 MB storage space</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Chrome className="w-4 h-4" />
                    Android
                  </h4>
                  <ul className="space-y-2 text-xs text-slate-500">
                    <li>• Android 8.0 or later</li>
                    <li>• Compatible with phones and tablets</li>
                    <li>• 45 MB storage space</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full p-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-slate-900 text-left">{faq.question}</span>
                  <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${
                    expandedFaq === index ? 'rotate-90' : ''
                  }`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}

            {/* Contact Support */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mt-8">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Still have questions?</h3>
              <p className="text-xs text-slate-500 mb-4">
                Our support team is available 24/7 to help you with any questions about the mobile app.
              </p>
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium">
                Contact Support
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
